import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule } from '@angular/material/tree';
import { ActivatedRoute, Router } from '@angular/router';
import { Category, Color, Product, ProductVariant, Size } from '@interfaces/interfaces';
import { CategoryTreeComponent } from '@micro-components/category-tree/category-tree.component';
import { FormErrorDisplayComponent } from '@micro-components/form-error-display/form-error-display.component';
import { ImageUploadComponent } from '@micro-components/image-upload/image-upload.component';
import { loadingProduct } from '@mockup_data/skeleton_data';
import { BffApiService } from '@shared/bff-api.service';
import { IDropdownSettings, NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { SnackBarService, successCreate, successUpdate } from 'src/app/shared/service/snack-bar.service';
import { NavigationService } from '../../shared/service/navigation.service';
import { ProductListService } from '../../shared/service/product-list.service';

@Component({
  selector: 'app-edit-article',
  templateUrl: './edit-article.component.html',
  styleUrls: ['./edit-article.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    FormErrorDisplayComponent,
    ReactiveFormsModule,
    NgMultiSelectDropDownModule,
    MatCheckboxModule,
    MatIconModule,
    MatTreeModule,
    CategoryTreeComponent,
    ImageUploadComponent,
  ],
  animations: [
    trigger('opacityLeave', [
      transition(':leave', [style({ opacity: 1 }), animate('100ms ease-in', style({ opacity: 0 }))]),
    ]),
  ],
})
export class EditArticleComponent implements OnInit {
  @ViewChild(CategoryTreeComponent) categoryTree!: CategoryTreeComponent;
  productVarianForm!: FormGroup;
  salesValues = [
    { name: '20%', value: 0.2 },
    { name: '30%', value: 0.3 },
    { name: '40%', value: 0.4 },
    { name: '50%', value: 0.5 },
    { name: '60%', value: 0.6 },
    { name: '70%', value: 0.7 },
    { name: '80%', value: 0.8 },
    { name: '90%', value: 0.9 },
  ];
  product!: Product;

  get variants(): FormArray {
    return this.productVarianForm.get('variants') as FormArray;
  }

  get isUpdateOfProduct(): boolean {
    return this.product.id !== undefined;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private snack: SnackBarService,
    private fb: FormBuilder,
    private bff: BffApiService,
    public nav: NavigationService,
    private prodService: ProductListService
  ) {} // constructor

  dropdownListMaterial: any = [];
  dropdownListProperties: any = [];
  dropdownListLinings: any = [];
  dropdownListCategories: Category[] = [
    {
      name: 'Loading...',
      id: 0,
      href: '#',
      isLoading: true,
      children: [{ name: 'Loading...', id: 0, href: '#', isLoading: true }],
    },
    {
      name: 'Loading...',
      id: 0,
      href: '#',
      isLoading: true,
      children: [{ name: 'Loading...', id: 0, href: '#', isLoading: true }],
    },
    {
      name: 'Loading...',
      id: 0,
      href: '#',
      isLoading: true,
      children: [{ name: 'Loading...', id: 0, href: '#', isLoading: true }],
    },
    {
      name: 'Loading...',
      id: 0,
      href: '#',
      isLoading: true,
      children: [{ name: 'Loading...', id: 0, href: '#', isLoading: true }],
    },
  ];
  backendListColors: Color[] = [
    { id: 0, name: 'Loading...', backgroundColor: '', ringColor: '', value: '', isLoading: true },
    { id: 0, name: 'Loading...', backgroundColor: '', ringColor: '', value: '', isLoading: true },
    { id: 0, name: 'Loading...', backgroundColor: '', ringColor: '', value: '', isLoading: true },
    { id: 0, name: 'Loading...', backgroundColor: '', ringColor: '', value: '', isLoading: true },
  ];
  backendListSizes: any = [];
  selectedMaterial: any = [];
  selectedProperties: any = [];
  selectedLinings: any = [];
  dropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    itemsShowLimit: 3,
    allowSearchFilter: true,
  };
  dropdownSettingsCategories: IDropdownSettings = {
    singleSelection: true,
    idField: 'id',
    textField: 'name',
    selectAllText: 'Select All',
    unSelectAllText: 'Unselect All',
    itemsShowLimit: 3,
    allowSearchFilter: true,
  };

  ngOnInit() {
    const productIdArg = this.route.snapshot.paramMap.get('id');
    const productId = productIdArg ? Number(productIdArg) : null;
    if (!productId) {
      this.product = this.prodService.getCurrentProduct();
    } else {
      this.product = loadingProduct;
      this.bff.getProducts(productId).subscribe((response) => {
        if (!response.data || response.data.length === 0) {
          this.router.navigate(['/dashboard/stocks']);
        } else {
          // Assigne the first product as the product to edit
          this.product = response.data[0];
          this.productVarianForm.patchValue(this.product);
          console.log(this.product);
        }
      });
    }

    this.productVarianForm = this.fb.group(
      {
        name: [this.product.name, Validators.required],
        external_id: [this.product.external_id, Validators.required],
        brand: [this.product.brand, Validators.required],
        category: [this.product.category, Validators.required],
        description: [this.product.description, Validators.required],
        old_price: [this.product.old_price, Validators.required],
        linings: [this.product.linings, Validators.required],
        properties: [this.product.properties, Validators.required],
        compositions: [this.product.compositions, Validators.required],
        wearer_type: [this.product.wearer_type, Validators.required],
        sale: [this.product.sale, Validators.required],
        variants: this.fb.array([]),
      },
      { validators: [this.uniqueVariantColors] }
    );

    this.bff.getPropreties().subscribe((res: any) => {
      this.dropdownListProperties = res;
    });

    this.bff.getMaterials().subscribe((res) => {
      this.dropdownListMaterial = res;
    });

    this.bff.getLinings().subscribe((res) => {
      this.dropdownListLinings = res;
    });

    this.bff.getCategories().subscribe((res: any) => {
      this.dropdownListCategories = res;
    });

    this.bff.getColors().subscribe((res: any) => {
      this.backendListColors = res;
    });

    this.bff.getSizes().subscribe((res: any) => {
      this.backendListSizes = res;

      if (this.product.variants.length == 0) {
        this.addVariant();
      } else {
        this.populateVariants(this.product.variants);
      }
    });
  }

  onCategoryTreeReady() {
    // Category might be null if coming from external stock
    if (this.categoryTree && this.product.category) {
      this.categoryTree.expandToCategoryById(this.product.category.id);
    }
  }

  getColorControl(index: number): FormControl {
    return this.variants.at(index).get('color') as FormControl;
  }

  getPhotoControl(index: number): FormControl {
    return this.variants.at(index).get('photos') as FormControl;
  }

  uniqueVariantColors(control: AbstractControl): ValidationErrors | null {
    const variants = control.get('variants') as FormArray;
    if (!variants) return null;

    // Reset possible previous errors
    variants.controls.forEach((variant) => {
      const colorControl = variant.get('color');
      if (colorControl?.hasError('duplicateColor')) {
        const otherErrors = { ...colorControl.errors };
        delete otherErrors['duplicateColor'];
        colorControl.setErrors(Object.keys(otherErrors).length ? otherErrors : null);
      }
    });

    // Ensure color usage is unique
    const colorUsed = new Map<string, AbstractControl>();
    for (const variant of variants.controls) {
      const colorControl = variant.get('color');
      const color = colorControl?.value;
      if (!color) continue;

      if (colorUsed.has(color)) {
        colorControl?.setErrors({ ...colorControl.errors, duplicateColor: true });
        colorUsed.get(color)?.setErrors({ ...colorUsed.get(color)!.errors, duplicateColor: true });
      } else {
        colorUsed.set(color, colorControl!);
      }
    }

    return null;
  }

  darkenColor(hex: string): string {
    let color = hex.startsWith('#') ? hex : '#' + hex;
    let r = parseInt(color.substring(1, 3), 16);
    let g = parseInt(color.substring(3, 5), 16);
    let b = parseInt(color.substring(5, 7), 16);

    r = Math.max(0, Math.floor(r * 0.85));
    g = Math.max(0, Math.floor(g * 0.85));
    b = Math.max(0, Math.floor(b * 0.85));

    return `rgb(${r}, ${g}, ${b})`;
  }

  addVariant(): void {
    const sizesGroup = this.fb.group({});
    this.backendListSizes.forEach((size: any) => {
      sizesGroup.addControl(size.id, this.fb.control(0, [Validators.required, Validators.min(0)]));
    });

    const variantFormGroup = this.fb.group({
      photos: [null, Validators.required],
      color: [null, Validators.required],
      sizes: sizesGroup,
      isNew: [true],
    });

    this.variants.push(variantFormGroup);
  }

  removeVariant(index: number): void {
    if (this.variants.length > 1) {
      this.variants.removeAt(index);
    }
  }

  populateVariants(variants: ProductVariant[]): void {
    variants.forEach((variant) => {
      const sizesGroup = this.fb.group({});

      const initialQuantities: Record<string, number> = {};

      this.backendListSizes.forEach((size: Size) => {
        const match = variant.sizes.find((s: Size) => s.id === size.id);
        const quantity = match ? match.quantity : 0;
        sizesGroup.addControl(size.id.toString(), this.fb.control(quantity, [Validators.required, Validators.min(0)]));
        initialQuantities[size.id] = quantity;
      });

      const variantFormGroup = this.fb.group({
        photos: [variant.photos, Validators.required],
        color: [{ value: variant.color.id, disabled: true }, Validators.required],
        sizes: sizesGroup,
        initialQuantities: [initialQuantities],
        isNew: [false],
      });

      this.variants.push(variantFormGroup);
    });
  }

  saveChanges() {
    // Ensure form is valid before posting it on the backend
    if (this.productVarianForm.invalid) {
      this.productVarianForm.markAllAsTouched();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Create the form that will be submited to the backend
      const formData = new FormData();
      // Populate form with the product informations (do variants below)
      formData.append('external_id', this.productVarianForm.get('external_id')?.value.id);
      formData.append('name', this.productVarianForm.get('name')?.value);
      formData.append('brand', this.productVarianForm.get('brand')?.value);
      formData.append('description', this.productVarianForm.get('description')?.value);
      formData.append('wearer_type', this.productVarianForm.get('wearer_type')?.value);
      formData.append('category', this.productVarianForm.get('category')?.value.id);
      formData.append('sale', this.productVarianForm.get('sale')?.value);

      // Handle the compositions
      const compositionsIds = this.productVarianForm.get('compositions')?.value.map((p: any) => p.id);
      for (const index in compositionsIds) {
        formData.append(`compositions[${index}]`, compositionsIds[index].toString());
      }

      // Handle the properties
      const propertiesIds = this.productVarianForm.get('properties')?.value.map((p: any) => p.id);
      for (const index in propertiesIds) {
        formData.append(`properties[${index}]`, propertiesIds[index].toString());
      }

      // Handle the linings
      const liningIds = this.productVarianForm.get('linings')?.value.map((p: any) => p.id);
      for (const index in liningIds) {
        formData.append(`linings[${index}]`, liningIds[index].toString());
      }

      const oldPrice = this.productVarianForm.get('old_price')?.value;
      formData.append('price', (oldPrice - oldPrice * this.productVarianForm.get('sale')?.value).toString());
      formData.append('old_price', oldPrice);

      // For each variants
      this.variants.controls.forEach((variant: any, i) => {
        const raw = variant.getRawValue();

        // Handle photos
        const files = this.variants.at(i).get('photos')?.value;
        files.forEach((file: any, fileIndex: number) => {
          if (file instanceof File) {
            formData.append(`variants[${i}][photos][${fileIndex}]`, file, file.name);
          } else if (typeof file === 'string') {
            formData.append(`variants[${i}][photos][${fileIndex}]`, file);
          }
        });

        // Handle color
        formData.append(`variants[${i}][color]`, raw.color);

        // Handle sizes
        Object.entries(raw.sizes as Record<string, string>).forEach(([key, value]) => {
          formData.append(`variants[${i}][sizes][${key}]`, value);
        });
      });

      // If the id is invalid, it's a new product to create
      if (this.isUpdateOfProduct === false) {
        this.bff.addProduct(formData).subscribe((data) => {
          this.snack.snack(successCreate);
          this.nav.goToStore();
        });
      } else {
        this.bff.editProductVariant(this.product.id, formData).subscribe((data) => {
          this.snack.snack(successUpdate);
          this.nav.goToStore();
        });
      }
    }
  }
}
